import { useCallback, useState } from 'react';
import * as DocumentPicker from 'utils/packages/documentPicker';
import ReactNativeBlobUtil from 'utils/packages/bloblUtil';
import { muteAll, useFetchOld } from 'hooks';
import { getTransactionsConfig, getTransactionsUploadUrl, processTransactions, } from 'actions';
import { Platform } from 'react-native';
import { isWeb } from '../../../constants';
// android uses MIME type, ios uses UTI
const supportedFileTypes = [
    'text/csv',
    'text/comma-separated-values',
    'public.comma-separated-values-text',
];
const TIMEOUT_DURATION = 60000; // 1 minute
export const useUploadCsvTransactions = () => {
    const [, , isFetching, fetchOld] = useFetchOld(muteAll);
    const [loading, setLoading] = useState(false);
    const pickWebFile = async () => new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        const cleanup = () => {
            if (document.body.contains(input)) {
                document.body.removeChild(input);
            }
        };
        input.onchange = (e) => {
            cleanup();
            const target = e.target;
            const file = target.files?.[0] || null;
            if (!file) {
                resolve(null);
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                reject(new Error('File too large'));
                return;
            }
            resolve(file);
        };
        input.oncancel = () => {
            cleanup();
            resolve(null);
        };
        document.body.appendChild(input);
        input.click();
    });
    const pickFile = async () => {
        try {
            const result = await DocumentPicker.pick({
                mode: 'open',
                allowMultiSelection: false,
                type: supportedFileTypes,
                allowVirtualFiles: true,
            });
            if (!result[0] || !result[0].size) {
                throw new Error('Incorrect file format');
            }
            if (result[0]?.size > 10 * 1024 * 1024) {
                throw new Error('File too large');
            }
            return result[0];
        }
        catch {
            return null;
        }
    };
    const prepareFileForUploadWeb = (file) => {
        try {
            if (!file) {
                throw new Error('Incorrect file format');
            }
            return {
                path: '',
                type: file.type || 'text/csv',
                name: file.name || 'transactions.csv',
                file,
            };
        }
        catch {
            return null;
        }
    };
    const prepareFileForUpload = (file) => {
        try {
            if (!file.uri || !file.type || !file.name) {
                throw new Error('Incorrect file format');
            }
            return {
                path: file.uri,
                type: file.type || 'text/csv',
                name: file.name || 'transactions.csv',
                file: undefined,
            };
        }
        catch {
            return null;
        }
    };
    const uploadAndGetConfig = useCallback(async () => {
        const file = isWeb ? await pickWebFile() : await pickFile();
        if (!file)
            return null;
        let localCopyPath = '';
        if (Platform.OS === 'android') {
            const localCopy = await DocumentPicker.keepLocalCopy({
                files: [
                    {
                        uri: file.uri,
                        fileName: file.name || 'transactions.csv',
                    },
                ],
                destination: 'documentDirectory',
            });
            if (localCopy && localCopy.length > 0 && localCopy[0]) {
                const localFile = localCopy[0];
                if (localFile.status === 'success' && localFile.localUri) {
                    localCopyPath = localFile.localUri;
                }
            }
        }
        const fileInfo = isWeb
            ? prepareFileForUploadWeb(file)
            : prepareFileForUpload(file);
        if (!fileInfo)
            return null;
        if (localCopyPath) {
            fileInfo.localCopyPath = localCopyPath;
        }
        let timeoutId;
        try {
            setLoading(true);
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`Upload timeout - operation took longer than ${TIMEOUT_DURATION / 1000} seconds`));
                }, TIMEOUT_DURATION);
            });
            const operationPromise = (async () => {
                const uploadUrlResponse = await fetchOld(getTransactionsUploadUrl(true));
                if (!uploadUrlResponse) {
                    throw new Error('Upload URL not created');
                }
                const { filename, uploadUrl } = uploadUrlResponse;
                const uploadResult = isWeb
                    ? await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': file.type || 'application/octet-stream',
                        },
                        body: file,
                        credentials: 'omit',
                        mode: 'cors',
                    })
                    : await ReactNativeBlobUtil.fetch('PUT', uploadUrl, { 'Content-Type': fileInfo.type }, ReactNativeBlobUtil.wrap(fileInfo.path));
                if (isWeb && 'status' in uploadResult) {
                    if (uploadResult.status < 200 || uploadResult.status >= 300) {
                        throw new Error(`Upload failed with status: ${uploadResult.status}`);
                    }
                }
                else if ('info' in uploadResult) {
                    if (uploadResult.info().status < 200 ||
                        uploadResult.info().status >= 300) {
                        throw new Error(`Upload failed with status: ${uploadResult.info().status}`);
                    }
                }
                else {
                    throw new Error(`Upload failed `);
                }
                const configResponse = await fetchOld(getTransactionsConfig(filename, true));
                if (!configResponse) {
                    throw new Error('Config not created');
                }
                if (configResponse.error.length > 0) {
                    throw new Error('File structure incompatible');
                }
                const { parseConfig } = configResponse;
                if (parseConfig.bookingDateColumnIndex === -1 ||
                    (parseConfig.creditAmountIndex === -1 &&
                        parseConfig.debitAmountIndex === -1) ||
                    parseConfig.counterpartyColumnIndex === -1) {
                    throw new Error('File structure incompatible');
                }
                return { config: configResponse, filename, fileInfo };
            })();
            const result = await Promise.race([operationPromise, timeoutPromise]);
            setLoading(false);
            clearTimeout(timeoutId);
            return result;
        }
        catch (error) {
            setLoading(false);
            clearTimeout(timeoutId);
            throw error;
        }
    }, [fetchOld]);
    const applyTransactions = useCallback(async (accountId, filename, config, muteAlert) => {
        const processResponse = await fetchOld(processTransactions(accountId, filename, config.parseConfig, muteAlert));
        if (!processResponse)
            throw new Error('Error importing transactions');
        return processResponse;
    }, [fetchOld]);
    return { uploadAndGetConfig, applyTransactions, isFetching, loading };
};
